import { useMemo } from 'react';
import { BREAKPOINTS, useIsBelow } from '~/lib/hooks/useMediaQuery';

/**
 * The animated atmosphere behind the empty-state hero: a radially-masked hairline
 * grid, a slow "breathing" lime glow, two drifting aurora blobs, and a scatter of
 * faint twinkling stars. Purely decorative (aria-hidden, pointer-events-none) and
 * fully paused under prefers-reduced-motion. Keyframes are scoped in the inline
 * <style> so this drops in without touching global CSS.
 */

const KEYFRAMES = `
@keyframes duHeroBreathe {0%,100%{opacity:.7;transform:translate(-50%,-50%) scale(1)}50%{opacity:1;transform:translate(-50%,-52%) scale(1.08)}}
@keyframes duHeroDrift1 {0%,100%{transform:translate(0,0)}50%{transform:translate(40px,30px)}}
@keyframes duHeroDrift2 {0%,100%{transform:translate(0,0)}50%{transform:translate(-36px,26px)}}
@keyframes duHeroTwinkle {0%,100%{opacity:.12}50%{opacity:.7}}
@media (prefers-reduced-motion: reduce){
  .du-hero-anim{animation:none !important}
}
`;

export function HeroBackground() {
  // Phones: fewer stars, no drifting blurred blobs, a static glow — the animated
  // blur layers are the expensive part on low-end GPUs and battery.
  const isPhone = useIsBelow(BREAKPOINTS.sm);
  const stars = useMemo(
    () =>
      Array.from({ length: isPhone ? 16 : 46 }, () => ({
        size: (Math.random() * 1.6 + 0.6).toFixed(2),
        left: (Math.random() * 100).toFixed(2),
        top: (Math.random() * 70).toFixed(2),
        dur: (Math.random() * 3 + 2.5).toFixed(1),
        delay: (Math.random() * 4).toFixed(1),
      })),
    [isPhone],
  );

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      <style>{KEYFRAMES}</style>

      {/* radially-masked hairline grid */}
      <div
        className="absolute inset-0 opacity-50"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.055) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,0.055) 1px, transparent 1px)',
          backgroundSize: '70px 70px',
          maskImage: 'radial-gradient(125% 80% at 50% 28%, #000 0%, #000 32%, transparent 78%)',
          WebkitMaskImage: 'radial-gradient(125% 80% at 50% 28%, #000 0%, #000 32%, transparent 78%)',
        }}
      />

      {/* drifting aurora blobs */}
      <div
        className="du-hero-anim absolute hidden rounded-full sm:block"
        style={{
          width: 360,
          height: 360,
          left: '12%',
          top: '16%',
          filter: 'blur(60px)',
          opacity: 0.5,
          background: 'radial-gradient(circle, rgba(217,242,106,0.10), transparent 70%)',
          animation: 'duHeroDrift1 22s ease-in-out infinite',
        }}
      />
      <div
        className="du-hero-anim absolute hidden rounded-full sm:block"
        style={{
          width: 320,
          height: 320,
          right: '10%',
          top: '28%',
          filter: 'blur(60px)',
          opacity: 0.5,
          background: 'radial-gradient(circle, rgba(106,184,242,0.06), transparent 70%)',
          animation: 'duHeroDrift2 26s ease-in-out infinite',
        }}
      />

      {/* twinkling stars */}
      {stars.map((s, i) => (
        <div
          key={i}
          className="du-hero-anim absolute rounded-full bg-white"
          style={{
            width: `${s.size}px`,
            height: `${s.size}px`,
            left: `${s.left}%`,
            top: `${s.top}%`,
            opacity: 0.12,
            animation: `duHeroTwinkle ${s.dur}s ease-in-out ${s.delay}s infinite`,
          }}
        />
      ))}

      {/* signature breathing lime glow */}
      <div
        className="du-hero-anim absolute"
        style={{
          left: '50%',
          top: '26%',
          width: 720,
          height: 520,
          transform: 'translate(-50%,-50%)',
          filter: 'blur(20px)',
          background: 'radial-gradient(ellipse 46% 44% at 50% 50%, rgba(217,242,106,0.10), transparent 60%)',
          animation: isPhone ? 'none' : 'duHeroBreathe 7s ease-in-out infinite',
        }}
      />
    </div>
  );
}
